import { useRef, useState, type FormEvent } from "react";
import { describeError } from "../api/errors";
import type { MixPreferenceInput, MixPreferenceView, MixView, Performance, Performer } from "../api/types";
import { useCommands, useGate, useServices } from "../app/services";
import { usePlaybackActions } from "../app/usePlaybackActions";
import { performerNames } from "../lib/labels";
import { formatMs } from "../lib/time";
import { isInFlight } from "../state/commands";

const FIELD_NAMES: Record<string, string> = { discovery: "曲との出会い", performer_ids: "歌い手", scene: "場面", mood: "雰囲気", reference_segment_ids: "参考曲" };
const VALUE_NAMES: Record<string, string> = { balanced: "おまかせのバランス", explore: "新しい曲を多めに", familiar: "なじみの曲を多めに", background: "作業・BGM", focused: "音楽を楽しむ", calm: "落ち着いた", bright: "明るい", energetic: "元気な" };
const targetOf = (mix: MixView, view: MixPreferenceView) => ({ mix_id: mix.mix_id, revision: mix.revision,
  preference_mix_id: view.mix_id, preference_revision: view.revision });
type Target = ReturnType<typeof targetOf>;

function valueLabel(field: string, value: unknown, view: MixPreferenceView) {
  if (field === "performer_ids") return view.selected_performers?.map((p) => p.name).join("、") || "指定なし";
  if (field === "reference_segment_ids") return view.selected_references?.map((p) => p.title).join("、") || "指定なし";
  return typeof value === "string" ? VALUE_NAMES[value] ?? value : "おまかせ";
}

/** Draft targets stay frozen while editing; an explicit review rebases stale input. */
export function MixPreferences({ mix, view, gateDescriptionId }: {
  mix: MixView; view: MixPreferenceView; gateDescriptionId?: string;
}) {
  const { api } = useServices();
  const actions = usePlaybackActions();
  const gate = useGate("mix");
  const commands = useCommands();
  const pendingCommand = commands.find((c) => ["mix_preference", "mix_preference_clear"].includes(c.kind) && isInFlight(c));
  const busy = pendingCommand !== undefined;
  const [target, setTarget] = useState<Target | null>(null);
  const [fields, setFields] = useState<NonNullable<MixPreferenceInput["fields"]>>({});
  const [text, setText] = useState("");
  const [mode, setMode] = useState<"merge" | "replace">("merge");
  const [minutes, setMinutes] = useState("");
  const [expiry, setExpiry] = useState<"restore" | "clear">("restore");
  const [query, setQuery] = useState("");
  const [performers, setPerformers] = useState<Performer[]>([]);
  const [pickedPerformers, setPickedPerformers] = useState<Performer[]>([]);
  const [referenceQuery, setReferenceQuery] = useState("");
  const [references, setReferences] = useState<Performance[]>([]);
  const [pickedReferences, setPickedReferences] = useState<Performance[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const searchRevision = useRef(0);
  const latest = targetOf(mix, view);
  const stale = target !== null && (target.mix_id !== latest.mix_id || target.revision !== latest.revision
    || target.preference_mix_id !== latest.preference_mix_id || target.preference_revision !== latest.preference_revision);
  const begin = () => setTarget((old) => old ?? latest);
  const duration = minutes === "" ? null : Number(minutes) * 60_000;
  const durationValid = duration === null || Number.isSafeInteger(duration) && duration > 0;
  const hasInput = Object.keys(fields).length > 0 || text.trim().length > 0;
  const select = (field: "discovery" | "scene" | "mood", value: string) => {
    begin();
    setFields((old) => { const next = { ...old }; if (!value) delete next[field];
      else Object.assign(next, { [field]: value === "clear" ? null : value }); return next; });
  };
  async function search(kind: "performer" | "reference") {
    const q = kind === "performer" ? query : referenceQuery;
    if (!q.trim()) return;
    const revision = ++searchRevision.current;
    setSearching(true); setSearchError(null);
    try {
      if (kind === "performer") {
        const results = await api.searchPerformers(q.trim());
        if (revision === searchRevision.current) setPerformers(results.slice(0, 20));
      } else {
        const results = await api.search(q.trim());
        if (revision === searchRevision.current) setReferences(results.performances.items.slice(0, 20));
      }
    } catch (error) { if (revision === searchRevision.current) setSearchError(describeError(error)); }
    finally { if (revision === searchRevision.current) setSearching(false); }
  }
  function submit(event: FormEvent) {
    event.preventDefault();
    if (!gate.ok || busy || stale || !hasInput || !durationValid) return;
    begin();
    actions.mixPreference(target ?? latest, { fields, free_text: text, mode, duration_ms: duration, expiry_action: expiry });
  }
  const reset = () => { setTarget(null); setFields({}); setText(""); setPickedPerformers([]); setPickedReferences([]);
    setMode("merge"); setMinutes(""); setExpiry("restore"); setQuery(""); setReferenceQuery("");
    setPerformers([]); setReferences([]); setSearchError(null); };

  return <section className="mix-preferences" aria-labelledby="mix-preferences-title">
    <h2 id="mix-preferences-title">Mixへの希望</h2>
    <p>普段はおまかせ。必要なときだけ、再生オーナーがこれからの選曲の希望を伝えられます。</p>
    {mix.pending_mix_id && <p>この希望は、再開を予約した次のMixに使います。</p>}
    {Object.keys(view.fields).length === 0 ? <p>現在の選曲はおまかせです。</p> :
      <dl className="mix-preferences__current">{Object.entries(view.fields).map(([name, value]) => {
        const receipt = view.requests.find((r) => r.current_fields.includes(name));
        const unsupported = view.unsupported_fields.includes(name);
        const status = unsupported ? "保存済み・反映機能は未接続" : receipt?.applied_fields?.includes(name) ? "反映開始"
          : receipt?.status === "applied" && receipt.applied_fields == null ? "項目別の反映は未確認" : "反映待ち";
        return <div key={name}><dt>{FIELD_NAMES[name] ?? name}</dt><dd>{valueLabel(name, value, view)}
          <span className="mix-preferences__status">{status}</span>
        </dd></div>;
      })}</dl>}
    <ul className="mix-preferences__receipts">{view.requests.map((receipt) => <li key={receipt.request_id}>
      {receipt.duration_ms !== null && <p>{receipt.status === "expired" ? "期間が終了しました。" :
        receipt.status === "superseded" ? "ほかの希望に置き換わっています。" : receipt.status === "applied" ?
          `残り再生時間 ${formatMs(receipt.remaining_ms ?? receipt.duration_ms)}` :
          `希望のいずれかが反映されてからの再生時間 ${formatMs(receipt.duration_ms)}（まだ計測していません）`}
        {receipt.expiry_action === "restore" ? " 期間後は前の希望に戻します。" : " 期間後はこの希望を解除します。"}</p>}
      {receipt.status === "applied" && receipt.duration_ms !== null && <p>この期間は、今回の希望のいずれかが反映された曲から数えています。項目ごとの反映状況は上の一覧で確認できます。</p>}
      {receipt.interpretation_method === "ai" && receipt.current_fields.length > 0 && <p>文章から読み取った希望です。選曲への反映状況は上の一覧で確認できます。</p>}
      {receipt.interpretation_notice && <p>{receipt.interpretation_notice}</p>}
      {receipt.unresolved.map((part, index) => <p className="mix-preferences__unresolved" key={index}>
        解釈できなかった部分：<q>{part}</q> <span>保存されていますが、選曲には反映していません。選択肢で指定するか、歌い手名・曲名などを具体的にして伝え直してください。</span>
      </p>)}
    </li>)}</ul>
    {busy && <p role="status">{pendingCommand.kind === "mix_preference_clear" ? "希望を解除しています。" : "希望を解釈・保存しています。文章をAIで読み取る場合は少し時間がかかります。"}</p>}
    {!gate.ok && !gateDescriptionId && <p>{gate.text}</p>}
    <details>
      <summary>希望を伝える・変更する</summary>
      <form onSubmit={submit} className="mix-preferences__form">
        <fieldset disabled={!gate.ok || busy} aria-describedby={!gate.ok ? gateDescriptionId : undefined}>
          <legend className="visually-hidden">Mixの希望を編集</legend>
          <label>曲との出会い<select value={fields.discovery === null ? "clear" : fields.discovery ?? ""} onChange={(e) => select("discovery", e.target.value)}>
            <option value="">変更しない</option><option value="balanced">おまかせのバランス</option>
            <option value="explore">新しい曲を多めに</option><option value="familiar">なじみの曲を多めに</option><option value="clear">この指定を解除</option>
          </select></label>
          <label>歌い手を探す<input value={query} onChange={(e) => setQuery(e.target.value)} maxLength={100} /></label>
          <button type="button" className="btn btn-secondary btn-small" disabled={searching || !query.trim()} onClick={() => search("performer")}>歌い手を検索</button>
          <ul className="mix-preferences__choices">{performers.map((person) => <li key={person.id}>
            <button type="button" disabled={pickedPerformers.some((p) => p.id === person.id) || pickedPerformers.length >= 20} onClick={() => {
              begin(); const next = [...pickedPerformers, person]; setPickedPerformers(next); setFields((old) => ({ ...old, performer_ids: next.map((p) => p.id) }));
            }}>{person.name}を選ぶ</button></li>)}</ul>
          {pickedPerformers.map((person) => <button className="btn btn-quiet btn-small" type="button" key={person.id} onClick={() => {
            begin(); const next = pickedPerformers.filter((p) => p.id !== person.id); setPickedPerformers(next); setFields((old) => ({ ...old, performer_ids: next.map((p) => p.id) }));
          }}>{person.name}を外す</button>)}
          <button className="btn btn-quiet btn-small" type="button" onClick={() => { begin(); setPickedPerformers([]); setFields((old) => ({ ...old, performer_ids: null })); }}>歌い手の指定を解除</button>
          <label>補足の希望<textarea maxLength={2000} rows={3} value={text} onChange={(e) => { begin(); setText(e.target.value); }} aria-describedby="mix-text-note" /></label>
          <p id="mix-text-note">{view.free_text_interpretation_available
            ? "「作業に集中したいから落ち着いた曲に」「歌い手名を多めに」など、場面・雰囲気・曲との出会い・歌い手・参考曲の希望を文章で伝えられます。読み取った内容と、解釈できなかった部分を一覧に表示します。選択肢でも指定した項目は、選択肢の内容を優先します。期間・追加・置換は下の項目で指定してください。"
            : "自由文の自動解釈はまだ接続されていません。入力は保存しますが、選曲には反映しません。反映できる部分は上の選択肢でも指定してください。"}</p>
          <details><summary>場面・雰囲気・参考曲を指定する</summary>
            <p>曲の音を調べて、これからの選曲に使います。参考曲の準備中は反映待ちになります。</p>
            <label>場面<select value={fields.scene === null ? "clear" : fields.scene ?? ""} onChange={(e) => select("scene", e.target.value)}>
              <option value="">変更しない</option><option value="background">作業・BGM</option><option value="focused">音楽を楽しむ</option><option value="clear">この指定を解除</option>
            </select></label>
            <label>雰囲気<select value={fields.mood === null ? "clear" : fields.mood ?? ""} onChange={(e) => select("mood", e.target.value)}>
              <option value="">変更しない</option><option value="calm">落ち着いた</option><option value="bright">明るい</option><option value="energetic">元気な</option><option value="clear">この指定を解除</option>
            </select></label>
            <label>参考曲を探す<input maxLength={100} value={referenceQuery} onChange={(e) => setReferenceQuery(e.target.value)} /></label>
            <button type="button" className="btn btn-secondary btn-small" disabled={searching || !referenceQuery.trim()} onClick={() => search("reference")}>登録済みの歌唱を検索</button>
            <ul className="mix-preferences__choices">{references.map((performance) => <li key={performance.segment.id}>
              <button type="button" disabled={pickedReferences.some((p) => p.segment.id === performance.segment.id) || pickedReferences.length >= 20} onClick={() => {
                begin(); const next = [...pickedReferences, performance]; setPickedReferences(next); setFields((old) => ({ ...old, reference_segment_ids: next.map((p) => p.segment.id) }));
              }}>{performance.work.title} / {performerNames(performance)}</button></li>)}</ul>
            {pickedReferences.map((performance) => <button type="button" className="btn btn-quiet btn-small" key={performance.segment.id} onClick={() => {
              begin(); const next = pickedReferences.filter((p) => p.segment.id !== performance.segment.id); setPickedReferences(next); setFields((old) => ({ ...old, reference_segment_ids: next.map((p) => p.segment.id) }));
            }}>{performance.work.title}を外す</button>)}
            <button className="btn btn-quiet btn-small" type="button" onClick={() => { begin(); setPickedReferences([]); setFields((old) => ({ ...old, reference_segment_ids: null })); }}>参考曲の指定を解除</button>
          </details>
          {searchError && <p role="alert">{searchError}</p>}
          <label>今の希望への加え方<select value={mode} onChange={(e) => { begin(); setMode(e.target.value as "merge" | "replace"); }}>
            <option value="merge">追加・変更（指定しない項目は残す）</option><option value="replace">置き換え（今回指定した希望だけにする）</option>
          </select></label>
          <label>希望を続ける再生時間（分）<input type="number" min="1" step="1" value={minutes} placeholder="指定なし" onChange={(e) => { begin(); setMinutes(e.target.value); }} aria-describedby="mix-duration-note" /></label>
          <p id="mix-duration-note">空欄なら変更・解除するか、Mixを終了するまで。今回の希望のいずれかが反映された曲からの再生時間で数え、一時停止・準備待ちの時間は含めません。</p>
          {minutes !== "" && <label>期間が終わったら<select value={expiry} onChange={(e) => { begin(); setExpiry(e.target.value as "restore" | "clear"); }}>
            <option value="restore">前の希望に戻す</option><option value="clear">この希望を解除する</option>
          </select></label>}
          {stale && <p role="status">編集中にMixまたは希望が更新されました。上の現在の希望を確認してください。
            <button type="button" className="btn btn-secondary btn-small" onClick={() => setTarget(latest)}>最新の状態を確認した</button></p>}
          <div className="controls__row"><button className="btn btn-primary" type="submit" disabled={stale || !hasInput || !durationValid}>希望を保存する</button>
            <button className="btn btn-quiet" type="button" onClick={reset}>入力をリセット</button></div>
        </fieldset>
      </form>
    </details>
    <button type="button" className="btn btn-secondary btn-small" disabled={!gate.ok || busy || view.requests.length === 0}
      aria-describedby={!gate.ok ? gateDescriptionId : undefined}
      onClick={() => actions.mixPreference(latest, null)}>希望をすべて解除しておまかせに戻す</button>
  </section>;
}
